import { useEffect, useRef, useState } from 'react';
import { ApiError, fetchMeta, generateDeckPdf, type DeckReport, type Meta } from './api';

const EXAMPLE_DECKLIST = `# Mazzo di esempio

// Legends (1)
1 Judy Álvarez — Braindance Maestro

// Units (2)
2 Corpo Security
`;

const PLACEHOLDER = `# Nome del mazzo

// Categoria (numero di carte)
1 Nome Carta
2 Altra Carta
...`;

type Status = 'idle' | 'loading' | 'success' | 'error';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to pick up the download before revoking the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function App() {
  const [decklist, setDecklist] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<DeckReport | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMeta()
      .then(setMeta)
      .catch(() => setMeta(null));
  }, []);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setDecklist(String(reader.result ?? ''));
      setStatus('idle');
      setError(null);
      setReport(null);
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleGenerate = async () => {
    if (!decklist.trim()) {
      setError('Incolla o carica prima una decklist.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    setReport(null);
    try {
      const { blob, filename, report } = await generateDeckPdf(decklist);
      triggerDownload(blob, filename);
      setReport(report);
      setStatus('success');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Errore imprevisto durante la generazione del PDF.');
      setStatus('error');
    }
  };

  const hasWarnings =
    report && (report.unresolved.length > 0 || report.parseWarnings.length > 0 || report.categoryWarnings.length > 0);

  return (
    <div className="page">
      <header className="header">
        <h1>
          Cyberpunk<span className="accent">Printer</span>
        </h1>
        <p className="subtitle">
          Carica la decklist, ottieni un PDF A4 pronto da stampare e ritagliare, con le carte in griglia.
        </p>
        {meta && (
          <p className="meta-line">
            {meta.totalCards} carte disponibili nel database · fino a {meta.maxCardsPerRequest} copie per richiesta
          </p>
        )}
      </header>

      <main className="panel">
        <div className="toolbar">
          <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()}>
            Carica file .txt
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,text/plain"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
          <button type="button" className="btn btn-ghost" onClick={() => setDecklist(EXAMPLE_DECKLIST)}>
            Usa esempio
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setDecklist('');
              setStatus('idle');
              setError(null);
              setReport(null);
            }}
          >
            Svuota
          </button>
        </div>

        <textarea
          className="decklist-input"
          value={decklist}
          onChange={(e) => setDecklist(e.target.value)}
          placeholder={PLACEHOLDER}
          spellCheck={false}
        />

        <button type="button" className="btn btn-primary" disabled={status === 'loading'} onClick={handleGenerate}>
          {status === 'loading' ? 'Generazione in corso…' : 'Genera PDF'}
        </button>

        {status === 'error' && error && <div className="banner banner-error">{error}</div>}

        {status === 'success' && report && (
          <div className="report">
            <div className="banner banner-success">
              PDF di “{report.deckTitle}” scaricato — {report.totalCardsPlaced}/{report.totalCardsParsed} carte,{' '}
              {report.pagesGenerated} pagina{report.pagesGenerated === 1 ? '' : 'e'} (griglia {report.grid.cols}×
              {report.grid.rows}).
            </div>

            {hasWarnings && (
              <div className="report-details">
                {report.categoryWarnings.length > 0 && (
                  <details open>
                    <summary>Avvisi sulle categorie ({report.categoryWarnings.length})</summary>
                    <ul>
                      {report.categoryWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </details>
                )}

                {report.parseWarnings.length > 0 && (
                  <details>
                    <summary>Righe non riconosciute ({report.parseWarnings.length})</summary>
                    <ul>
                      {report.parseWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </details>
                )}

                {report.unresolved.length > 0 && (
                  <details open>
                    <summary>Carte escluse dal PDF ({report.unresolved.length})</summary>
                    <ul>
                      {report.unresolved.map((u, i) => (
                        <li key={i}>
                          [{u.category}] {u.qty}× “{u.name}” —{' '}
                          {u.reason === 'not-found'
                            ? 'nessuna carta con questo nome nel database'
                            : 'carta trovata, ma nessuna immagine disponibile'}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {report.looseMatchCount > 0 && (
                  <p className="note">
                    {report.looseMatchCount} carta/e abbinate per corrispondenza approssimata (accenti/trattini) —
                    verifica che siano corrette.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="footer">
        <p>
          Formato decklist: <code># Titolo mazzo</code>, <code>// Categoria (N)</code>,{' '}
          <code>qty Nome Carta</code> su ogni riga.
        </p>
      </footer>
    </div>
  );
}
